import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getSalesManagers,
  searchAccountUsers,
  updateContractManagers,
  updateAccountSalesManager,
  getAccountMembersForPicker,
  getAccountContactRoles,
  updateMemberTags,
} from '../api';
import type { Account, AccountDashboardData } from '../types';

/**
 * Canonical CM / TM / IT contacts for an account.
 * Reads contact_roles JOIN contacts via /v4/accounts/:id/contact-roles.
 * Replaces the legacy pattern of filtering useAccountMembersForPicker by
 * usergroup_members.tags, which missed contacts who aren't usergroup_members.
 */
export function useAccountContactRoles(accountId: string) {
  return useQuery({
    queryKey: ['account-contact-roles', accountId],
    queryFn: () => getAccountContactRoles(accountId),
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch ATEK staff with sales_manager role
 */
export function useSalesManagers() {
  return useQuery({
    queryKey: ['sales-managers'],
    queryFn: getSalesManagers,
    staleTime: 5 * 60 * 1000, // 5 min - staff list changes rarely
  });
}

/**
 * Search users for contractual manager picker (debounced by caller)
 */
export function useSearchAccountUsers(accountId: string, query: string) {
  return useQuery({
    queryKey: ['account-users-search', accountId, query],
    queryFn: () => searchAccountUsers(accountId, query),
    enabled: query.length >= 2,
    staleTime: 30 * 1000,
  });
}

/**
 * Mutation to update sales manager or contractual managers on a contract
 */
export function useUpdateContractManagers(accountId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      data,
    }: {
      contractId: string;
      data: { salesManagerId?: string; contractualManagerIds?: string[] };
    }) => updateContractManagers(accountId, contractId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
    },
  });
}

/**
 * Mutation to update account-level sales manager (Account Manager)
 */
export function useUpdateAccountSalesManager(accountId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (salesManagerId: string | null) =>
      updateAccountSalesManager(accountId, salesManagerId),
    onSuccess: (result) => {
      if (result.account) {
        queryClient.setQueryData<AccountDashboardData>(
          ['account-dashboard', accountId],
          (current) => (
            current
              ? { ...current, account: { ...current.account, ...result.account } }
              : current
          ),
        );
        queryClient.setQueryData<Account>(['account', accountId], result.account);
      }

      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['accounts-table'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Account manager updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update account manager');
    },
  });
}

/**
 * Fetch all enabled account members for the contractual manager picker
 */
export function useAccountMembersForPicker(accountId: string) {
  return useQuery({
    queryKey: ['account-members-picker', accountId],
    queryFn: () => getAccountMembersForPicker(accountId),
    staleTime: 60 * 1000, // 1 min
  });
}

/**
 * Mutation to update member tags (e.g. "Contractual Manager")
 */
export function useUpdateMemberTags(accountId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tag, memberIds }: { tag: string; memberIds: string[] }) =>
      updateMemberTags(accountId, tag, memberIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-members-picker', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-contact-roles', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      // Also invalidate the users tab data
      queryClient.invalidateQueries({ queryKey: ['account-users', accountId] });
    },
  });
}
